import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useCallback, useEffect, useState } from 'react';
import config from '../../config';

import { LoginUrlParams, useAuth } from '../../common/Auth/useAuth';
import { isSupportedLocale } from '../../i18n';
import { isValidLocale, setWindowLocation } from '../../common/helpers';
import { useNavigation } from '../Common/useNavigation';
import LoginForm from '../Common/LoginForm';
import UI from '../../common/UI';
import useDocumentTitle from '../../common/useDocumentTitle';
import useUrlState from '../../common/useUrlState';

const LoginPage = () => {
  const { t } = useTranslation();
  const { r } = useNavigation();
  const history = useHistory();

  useDocumentTitle([
    t('log_in'),
    t('dashboard'),
    'Atleta',
  ]);

  const [urlState, setUrlState] = useUrlState({
    parse: (params, { string }): LoginUrlParams => ({
      returnUrl: string(params.returnUrl),
      email: string(params.email),
      loginRequestedForEmail: string(params.loginRequestedForEmail),
      locale: isValidLocale(params.locale) && isSupportedLocale(params.locale) ? params.locale : null,
    }),
  });

  // Retrieve the email from the URL and remember it.
  const [loginRequestedForEmail] = useState(urlState.loginRequestedForEmail);

  // Remove the email from the URL.
  useEffect(() => {
    if (urlState.loginRequestedForEmail) {
      setUrlState((params) => ({
        ...params,
        loginRequestedForEmail: null,
      }), true);
    }
  }, [urlState.loginRequestedForEmail, setUrlState]);

  const redirect = useCallback(() => {
    let returnUrl: URL | null;

    try {
      returnUrl = urlState.returnUrl ? new URL(urlState.returnUrl) : null;
    } catch (_) {
      history.replace(r('Portal'));
      return;
    }

    if (returnUrl?.origin === new URL(config.baseUrl).origin) {
      setWindowLocation(returnUrl.href);
    } else {
      history.replace(r('Portal'));
    }
  }, [r, urlState.returnUrl, history]);

  const { user, loading } = useAuth({
    onAuthenticated: redirect,
  });

  if (loading || user) {
    return (
      <UI.PageLoader />
    );
  }

  return (
    <UI.Card sc={{ padding: [4, 6], mx: [3, 4] }}>
      <LoginForm scopedEmail={urlState.email} loginRequestedForEmail={loginRequestedForEmail} />
    </UI.Card>
  );
};

export default LoginPage;
